import { Select, MenuItem, Checkbox, TextField, ListSubheader } from "@mui/material";

import React, { useEffect, useRef, useState } from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import styles from "./style.module.css";

const MultiSelect = ({ options, setFormData, formData,id }) => {
  const extractCohortIds = (data) => {
    return data.map((item) => item.cohort_id);
  };
  const extractSectionIds = (data) => {
    const sectionIds = [];
    data.forEach((item) => {
      item.sections.forEach((section) => {
        sectionIds.push(section.section_id);
      });
    });
    return sectionIds;
  };
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  // True once the admin changes the selection; until then the loaded sections are kept as they are
  const touched = useRef(false);
  const previousPhaseId = useRef(id);

  const toggleCohort = (cohortId) => {
    touched.current = true;
    if (selectedCohorts.includes(cohortId)) {
      setSelectedCohorts(selectedCohorts.filter((id) => id !== cohortId));
      setSelectedSections(
        selectedSections.filter(
          (sectionId) => !isSectionInCohort(sectionId, cohortId)
        )
      );
    } else {
      setSelectedCohorts([...selectedCohorts, cohortId]);
      const cohortSections = getCohortSections(cohortId);
      setSelectedSections([...selectedSections, ...cohortSections]);
    }
  };

  const toggleSection = (sectionId) => {
    touched.current = true;
    const sectionCohortId = getSectionCohortId(sectionId);
    if (selectedSections.includes(sectionId)) {
      setSelectedSections(selectedSections.filter((id) => id !== sectionId));
    } else {
      setSelectedSections([...selectedSections, sectionId]);
    }

    const cohortSections = getCohortSections(sectionCohortId);

    let isCohortSelected = true;
    for (let i = 0; i < cohortSections.length; i++) {
      if (selectedSections.includes(cohortSections[i])) {
        isCohortSelected = true;
        break;
      }
    }

    if (isCohortSelected) {
      setSelectedCohorts([...selectedCohorts, sectionCohortId]);
    } else {
      setSelectedCohorts(
        selectedCohorts.filter((id) => id !== sectionCohortId)
      );
    }
  };

  const isCohortChecked = (cohortId) => selectedCohorts.includes(cohortId);
  const isSectionChecked = (sectionId) => selectedSections.includes(sectionId);
  // Show the workshop's saved sections as ticked (they can arrive after this opens)
  useEffect(() => {
    if (touched.current) return;
    setSelectedCohorts(extractCohortIds(formData.workshop_sections));
    setSelectedSections(extractSectionIds(formData.workshop_sections));
  }, [formData.workshop_sections]);
  const getCohortSections = (cohortId) => {
    const cohort = options.find((data) => data.cohort_id === cohortId);
    return cohort ? cohort.sections.map((section) => section.section_id) : [];
  };

  const getSectionCohortId = (sectionId) => {
    for (const cohort of options) {
      if (cohort.sections.some((section) => section.section_id === sectionId)) {
        return cohort.cohort_id;
      }
    }
    return null;
  };

  const isSectionInCohort = (sectionId, cohortId) => {
    const cohortSections = getCohortSections(cohortId);
    return cohortSections.includes(sectionId);
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredOptions = !normalizedSearch
    ? options
    : options
        .map((cohort) => {
          const cohortMatches = cohort.cohort_name
            ?.toLowerCase()
            .includes(normalizedSearch);
          if (cohortMatches) return cohort;
          const matchingSections = cohort.sections.filter(
            (section) =>
              section.section_name?.toLowerCase().includes(normalizedSearch) ||
              section.course_name?.toLowerCase().includes(normalizedSearch)
          );
          return matchingSections.length > 0
            ? { ...cohort, sections: matchingSections }
            : null;
        })
        .filter(Boolean);

  const sections = filteredOptions.map((sectionData) => (
    <li key={sectionData.cohort_id}>
      <FormControlLabel
        control={
          <Checkbox
            checked={isCohortChecked(sectionData.cohort_id)}
            onChange={() => toggleCohort(sectionData.cohort_id)}
          />
        }
        label={sectionData.cohort_name}
      />
      <br />
      <ul>
        {sectionData.sections.map((section) => (
          <li key={section.section_id}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSectionChecked(section.section_id)}
                  onChange={() => toggleSection(section.section_id)}
                />
              }
              label={`${section.section_name}   (${section.course_name}) `}
            />
          </li>
        ))}
      </ul>
    </li>
  ));

  const generateWorkshopSections = (selectedCohorts, selectedSections) => {
    const filteredCohorts = options.filter((item) => {
      return selectedCohorts.includes(item.cohort_id);
    });

    const workshopSections = filteredCohorts.map((cohort) => {
      const sections = cohort.sections.filter((section) =>
        selectedSections.includes(section.section_id)
      );
      return { ...cohort, sections };
    });

    return workshopSections;
  };

  const selectedWorkshopSections = generateWorkshopSections(
    selectedCohorts,
    selectedSections
  );

  // Only write back the admin's own changes, so opening Edit can't wipe the saved sections
  useEffect(() => {
    if (!touched.current) return;
    setFormData((prev) => ({
      ...prev,
      workshop_sections: selectedWorkshopSections,
    }));
  }, [selectedCohorts, selectedSections]);

  // Cohorts belong to a phase: clear the selection only when the admin switches to another phase
  useEffect(() => {
    const phaseChanged =
      previousPhaseId.current != null && previousPhaseId.current !== id;
    previousPhaseId.current = id;
    if (!phaseChanged) return;
    touched.current = true;
    setSelectedCohorts([]);
    setSelectedSections([]);
    setSearchTerm("");
    setFormData((prev) => ({ ...prev, workshop_sections: [] }));
  }, [id]);

  return (
    <>
      <Select fullWidth value={1} MenuProps={{ autoFocus: false }}>
        <MenuItem value={1}>
          <em>Select Cohorts and Sections</em>
        </MenuItem>
        <ListSubheader sx={{ p: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search cohort or section..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Escape") {
                e.stopPropagation();
              }
            }}
          />
        </ListSubheader>
        <ul className={styles.phaseCohortList}>{sections}</ul>
      </Select>
    </>
  );
};

export default MultiSelect;
